import * as React from "react";
import { NumericFormat } from "react-number-format";
import { Input } from "./input";
import { cn } from "../../lib/utils";

type FormatType = "currency" | "percentage" | "number";

interface FormattedInputProps extends Omit<
  React.ComponentProps<"input">,
  "value" | "onChange"
> {
  value: string;
  onChange: (value: string) => void;
  formatType?: FormatType;
  allowNegative?: boolean;
  className?: string;
  /** Ref to the underlying input element (NumericFormat forwards no ref). */
  getInputRef?: React.Ref<HTMLInputElement>;
}

function FormattedInput({
  value,
  onChange,
  formatType,
  allowNegative: allowNegativeProp,
  className,
  type,
  id,
  placeholder,
  disabled,
  getInputRef,
  ...props
}: FormattedInputProps) {
  const handleValueChange = React.useCallback(
    (
      values: { floatValue: number | undefined; formattedValue: string },
      sourceInfo: { source: string },
    ) => {
      // Only respond to user input, not programmatic value prop changes
      if (sourceInfo.source === "prop") return;
      // Convert to string, handling undefined/null
      const stringValue = values.floatValue?.toString() || "";
      onChange(stringValue);
    },
    [onChange],
  );

  // Convert string value to number for NumberFormat
  const numericValue = React.useMemo(() => {
    if (!value || value === "") return undefined;
    const num = parseFloat(value);
    return isNaN(num) ? undefined : num;
  }, [value]);

  // Extract only the props that NumericFormat accepts
  // NumericFormat only accepts type: "text" | "password" | "tel"
  const numericFormatProps: {
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    type?: "text" | "password" | "tel";
  } = {
    id,
    placeholder,
    disabled,
    // NumericFormat requires type="text", not "number" or other types
    type: "text",
  };

  if (formatType === "currency") {
    return (
      <NumericFormat
        {...numericFormatProps}
        customInput={Input}
        getInputRef={getInputRef}
        value={numericValue}
        onValueChange={handleValueChange}
        thousandSeparator={true}
        prefix="$"
        decimalScale={2}
        fixedDecimalScale={true}
        allowNegative={allowNegativeProp ?? true}
        className={cn(className)}
      />
    );
  }

  if (formatType === "percentage") {
    return (
      <NumericFormat
        {...numericFormatProps}
        customInput={Input}
        getInputRef={getInputRef}
        value={numericValue}
        onValueChange={handleValueChange}
        suffix="%"
        decimalScale={2}
        fixedDecimalScale={true}
        allowNegative={false}
        className={cn(className)}
      />
    );
  }

  // Whole number with thousands separators (quantities, counts).
  if (formatType === "number") {
    return (
      <NumericFormat
        {...numericFormatProps}
        customInput={Input}
        getInputRef={getInputRef}
        value={numericValue}
        onValueChange={handleValueChange}
        thousandSeparator={true}
        decimalScale={0}
        allowNegative={allowNegativeProp ?? false}
        className={cn(className)}
      />
    );
  }

  // Fallback to regular input if no format type
  const { ref: _ref, ...restProps } = props as React.ComponentProps<"input">;
  return (
    <Input
      {...restProps}
      id={id}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(className)}
    />
  );
}

export { FormattedInput };
export type { FormattedInputProps, FormatType };
